import { createRng, nextInt } from '../core/rng';
import { createHand } from '../hand/init';
import { advance } from '../hand/advance';
import { legalActions } from '../hand/legal';
import { dealForSpec } from '../hand/deal';
import { assignPositions } from '../hand/positions';
import { scriptVillainAction } from './villain';
import { classifySpot } from './spot';
/** Stop the instant hero must act - the pure §3 behavior. */
export const stopAtFirstDecision = () => ({ stop: true });
/**
 * A minimal, documented preflop line so postflop spots can be targeted: hero checks when able, opens to
 * ~2.5bb when folded to (only blinds in), otherwise flat-calls a raise (becomes the single-raised-pot
 * defender). Stops at hero's first postflop decision. This is scaffolding for spot reachability, NOT
 * hero strategy - the returned decision point is where the S7 coach actually reasons.
 */
export const preflopOpenCallAutopilot = (state, _deal, heroSeat) => {
    if (state.street !== 'preflop')
        return { stop: true };
    const legal = legalActions(state);
    const player = state.players.find((p) => p.seat === heroSeat);
    if (!player)
        return { stop: true };
    const toCall = state.currentBet - player.committedThisStreet;
    const has = (k) => legal.find((l) => l.kind === k);
    if (toCall <= 0 && has('check'))
        return { seat: heroSeat, kind: 'check' };
    const bb = state.spec.stakes.bb;
    const raiseSlot = has('raise');
    if (state.currentBet <= bb && raiseSlot) {
        // Round the ~2.5bb open target to whole dollars BEFORE clamping, mirroring the preflop convention
        // (`preflopIntentToAction`: `Math.round(toBB * bb)`, ~villain.ts:172) and CX1's postflop fix
        // (villain.ts:121). At $1/$3 an unrounded `bb * 2.5` = $7.50 poisons every hand dealt through the
        // Observe/GH (non-`preflopPlay`) path with fractional money. Round the fallback max default too, so
        // a missing `raiseSlot.max` cannot re-introduce a float; clamp bounds are whole once all bets are
        // whole, so the result stays whole (WORKING_STATE CX1 carried note, 2026-07-16).
        const openTarget = Math.round(bb * 2.5);
        const openTo = Math.min(Math.max(openTarget, raiseSlot.min ?? 0), raiseSlot.max ?? openTarget);
        return { seat: heroSeat, kind: 'raise', amount: openTo };
    }
    if (has('call'))
        return { seat: heroSeat, kind: 'call' };
    return { stop: true };
};
const MAX_ATTEMPTS = 50;
/** Resolve the button seat: honor a pinned heroPosition, else pick deterministically from the seed. */
const resolveButtonSeat = (spec, n, rng) => {
    if (spec.heroPosition) {
        for (let b = 0; b < n; b++) {
            if (assignPositions(n, b).get(spec.heroSeat) === spec.heroPosition)
                return b;
        }
        throw new RangeError(`heroPosition '${spec.heroPosition}' unreachable for hero seat ${spec.heroSeat} on a ${n}-handed table`);
    }
    return nextInt(rng, n);
};
const archetypeAt = (state, seat, deps) => {
    const player = state.players.find((p) => p.seat === seat);
    if (!player?.archetypeId)
        throw new Error(`no archetype for villain seat ${seat}`);
    return deps.archetype(player.archetypeId);
};
/** Deal once from `spec.seed` and drive villains (and any hero autopilot) to hero's decision or hand end. */
const playOneDeal = (spec, deps, autopilot, startingStacks) => {
    const n = spec.villains.length + 1;
    const buttonSeat = resolveButtonSeat(spec, n, createRng(`${spec.seed}::setup`));
    const deal = dealForSpec(spec, n);
    const villainRng = createRng(`${spec.seed}::villain`);
    let state = createHand({ handId: `gen:${spec.seed}`, spec, buttonSeat, startingStacks });
    const villainActions = [];
    const heroAutoActions = [];
    const guard = n * 40; // betting is finite; this only backstops an unforeseen non-terminating loop.
    for (let i = 0; i < guard; i++) {
        if (state.phase !== 'acting' || state.toAct === null) {
            return { state, villainActions, heroAutoActions, deal, buttonSeat, heroDecided: false };
        }
        if (state.toAct === spec.heroSeat) {
            const decision = autopilot(state, deal, spec.heroSeat);
            if ('stop' in decision) {
                return { state, villainActions, heroAutoActions, deal, buttonSeat, heroDecided: true };
            }
            heroAutoActions.push(decision);
            state = advance(state, decision);
        }
        else {
            const act = scriptVillainAction(state, state.toAct, archetypeAt(state, state.toAct, deps), deal, villainRng, deps.villainPreflop);
            villainActions.push(act);
            state = advance(state, act);
        }
    }
    throw new Error(`playOneDeal: exceeded ${guard} actions without terminating (seed '${spec.seed}')`);
};
/** Build the DecisionPoint the UI/coach binds to from a halted-at-hero HandState. */
const toDecisionPoint = (state, decisionIndex) => {
    const heroSeat = state.spec.heroSeat;
    const hero = state.players.find((p) => p.seat === heroSeat);
    if (!hero)
        throw new Error(`toDecisionPoint: hero seat ${heroSeat} not found`);
    const pot = state.pots.reduce((sum, p) => sum + p.amount, 0);
    const toCall = state.currentBet - hero.committedThisStreet;
    const others = state.players.filter((p) => p.seat !== heroSeat && !p.folded);
    const maxOther = others.length ? Math.max(...others.map((p) => p.stack)) : 0;
    const effectiveStack = Math.min(hero.stack, maxOther);
    return {
        handId: state.handId,
        street: state.street,
        decisionIndex,
        state,
        legal: legalActions(state),
        pot,
        toCall,
        effectiveStack,
        spr: pot > 0 ? effectiveStack / pot : 0,
    };
};
/** Assemble a GeneratedScenario result from a played deal. */
const finalize = (spec, played, attempts) => {
    const spotOf = classifySpot(played.state);
    const weight = spec.spotWeights?.[spotOf] ?? 0;
    return {
        spec,
        decisionPoint: toDecisionPoint(played.state, played.heroAutoActions.length),
        buttonSeat: played.buttonSeat,
        villainActions: played.villainActions,
        heroAutoActions: played.heroAutoActions,
        attempts,
        spotOf,
        matchedSpot: weight > 0 ? spotOf : null,
    };
};
/**
 * Generate a hero decision from `spec`. With no spotWeights, returns the first deal that reaches a hero
 * decision. With spotWeights, rejection-samples deals (each attempt reseeds the deal via a seed suffix,
 * ENGINE_DESIGN §3.2's `rng.fork(attempt)`) and accepts one landing in a weighted family - probabilities
 * proportional to weight/maxWeight - falling back to the first hero decision seen after `maxAttempts`.
 */
export const generate = (spec, deps, opts = {}) => {
    const autopilot = opts.heroAutopilot ?? stopAtFirstDecision;
    const attemptsCap = opts.maxAttempts ?? MAX_ATTEMPTS;
    const weights = spec.spotWeights;
    const maxWeight = weights ? Math.max(0, ...Object.values(weights)) : 0;
    let fallback = null;
    for (let a = 0; a < attemptsCap; a++) {
        const derived = { ...spec, seed: `${spec.seed}#a${a}` };
        const played = playOneDeal(derived, deps, autopilot, opts.startingStacks);
        if (!played.heroDecided)
            continue;
        if (!weights || maxWeight <= 0)
            return finalize(derived, played, a + 1);
        const spot = classifySpot(played.state);
        const weight = weights[spot] ?? 0;
        if (weight > 0) {
            const accept = createRng(`${derived.seed}::accept`).next();
            if (accept < weight / maxWeight)
                return finalize(derived, played, a + 1);
        }
        if (!fallback)
            fallback = played;
    }
    if (fallback) {
        // Exhausted the budget without ACCEPTING a weighted deal - take the first hero decision we saw.
        // `attempts === maxAttempts` is what identifies this path; matchedSpot does NOT, and is not
        // necessarily null: `fallback` is latched below a failed accept ROLL too, so a deal that did land in
        // a low-weight family (hit often, accepted rarely) returns that family as matchedSpot, per the field's
        // contract ("spotOf when it is a positive-weight target"). Null here only means the deal we settled
        // for was in an unweighted family.
        // The seed MUST be the one that actually dealt this hand (`${spec.seed}#aK`), never a synthetic
        // '#fallback' marker: consumers treat the returned spec as the deal authority and re-deal from it
        // (liveRunner's `dealForSpec(scenario.spec, n)`), so a seed that never dealt the hand silently
        // reproduces DIFFERENT cards than the decisionPoint we return alongside it.
        const derived = { ...spec, seed: fallback.state.spec.seed };
        return finalize(derived, fallback, attemptsCap);
    }
    throw new Error(`generate: no hero decision reached in ${attemptsCap} attempts for seed '${spec.seed}'`);
};
const sameDeal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/**
 * "Same Hand, Different Villain" (ARCHITECTURE.md killer feature 3): run the identical (spec, seed) with
 * one villain's archetype swapped. Both share the deal (dealForSpec depends only on seed + table size,
 * not archetype), so identical cards fall; only the scripted line - and, downstream, the coach's
 * advice - diverges. Spot targeting is off here (it is orthogonal to the demo); the two are scanned on
 * the SAME attempt index so the shared deal is guaranteed even when the differing lines end at different
 * points.
 */
export const sameHandDifferentVillain = (spec, deps, swap, opts = {}) => {
    const attemptsCap = opts.maxAttempts ?? MAX_ATTEMPTS;
    const swapped = spec.villains.map((v) => (v.seat === swap.seat ? { ...v, archetypeId: swap.archetypeId } : v));
    for (let a = 0; a < attemptsCap; a++) {
        const seed = `${spec.seed}#a${a}`;
        const baseSpec = { ...spec, seed, spotWeights: undefined };
        const variantSpec = { ...spec, seed, spotWeights: undefined, villains: swapped };
        const basePlayed = playOneDeal(baseSpec, deps, stopAtFirstDecision);
        const variantPlayed = playOneDeal(variantSpec, deps, stopAtFirstDecision);
        if (basePlayed.heroDecided && variantPlayed.heroDecided) {
            return {
                base: finalize(baseSpec, basePlayed, a + 1),
                variant: finalize(variantSpec, variantPlayed, a + 1),
                identicalCards: sameDeal(basePlayed.deal, variantPlayed.deal),
            };
        }
    }
    throw new Error(`sameHandDifferentVillain: no shared hero decision in ${attemptsCap} attempts for seed '${spec.seed}'`);
};
//# sourceMappingURL=generate.js.map